import React, { useState } from 'react';
import { Tv, Film, Layers, Play } from 'lucide-react';
import { getProxiedImageUrl } from '../../services/iptvService';

interface ChannelLogoProps {
  logo?: string;
  name: string;
  category?: string;
  userAgent?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  type?: 'live' | 'movie' | 'series';
}

// Generate deterministic gradient background based on channel name
function getDeterministicGradient(name: string): string {
  const gradients = [
    'from-red-600/30 to-slate-900 border-red-500/30 text-red-300',
    'from-blue-600/30 to-slate-900 border-blue-500/30 text-blue-300',
    'from-emerald-600/30 to-slate-900 border-emerald-500/30 text-emerald-300',
    'from-amber-600/30 to-slate-900 border-amber-500/30 text-amber-300',
    'from-purple-600/30 to-slate-900 border-purple-500/30 text-purple-300',
    'from-cyan-600/30 to-slate-900 border-cyan-500/30 text-cyan-300',
    'from-rose-600/30 to-slate-900 border-rose-500/30 text-rose-300',
    'from-indigo-600/30 to-slate-900 border-indigo-500/30 text-indigo-300'
  ];
  
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % gradients.length;
  return gradients[index];
}

// Extract smart short channel badge (e.g., "TV3", "LA1", "DAZN", "ANT3", "M+ DEPORTES" -> "M+ DEP")
function getChannelBadge(name: string): string {
  if (!name) return 'TV';
  
  // Clean common prefixes/suffixes
  const clean = name
    .replace(/^(ES|CAT|ESP|UK|US|FR|DE|IT|PT|AR|MX|CO)\s*[:|-]\s*/i, '')
    .replace(/\b(1080p|720p|4k|fhd|hd|hevc|h265|sd|raw)\b/gi, '')
    .replace(/[|()\[\]]/g, ' ')
    .trim();

  const words = clean.split(/\s+/).filter(Boolean);
  if (words.length === 1) {
    return words[0].slice(0, 5).toUpperCase();
  }
  if (words.length === 2) {
    if (words[0].length <= 3) {
      return `${words[0]} ${words[1].slice(0, 3)}`.toUpperCase();
    }
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return words.slice(0, 3).map(w => w[0]).join('').toUpperCase();
}

export const ChannelLogo: React.FC<ChannelLogoProps> = ({
  logo,
  name,
  category,
  userAgent,
  className = '',
  size = 'md',
  type = 'live'
}) => {
  const [imageFailed, setImageFailed] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const proxiedUrl = logo && !imageFailed ? getProxiedImageUrl(logo, userAgent) : '';
  const gradientClass = getDeterministicGradient(name || 'IPTV');
  const badgeText = getChannelBadge(name || '');

  const sizeClasses = {
    sm: 'w-8 h-8 text-[10px]',
    md: 'w-full h-20 text-xs sm:text-sm',
    lg: 'w-16 h-16 text-sm'
  }[size];

  return (
    <div className={`relative flex items-center justify-center rounded-xl overflow-hidden bg-slate-950/60 select-none ${sizeClasses} ${className}`}>
      {/* 1. Proxied Image if available */}
      {proxiedUrl && (
        <img
          src={proxiedUrl}
          alt={name}
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          onError={() => setImageFailed(true)}
          className={`max-h-full max-w-full object-contain filter drop-shadow transition-all duration-300 ${
            imageLoaded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
          }`}
        />
      )}

      {/* 2. Fallback Graphic Badge (Shown if no logo, loading, or failed) */}
      {(!proxiedUrl || !imageLoaded) && (
        <div className={`absolute inset-0 flex flex-col items-center justify-center p-2 bg-gradient-to-br border ${gradientClass} transition-opacity duration-200`}>
          <div className="flex items-center gap-1 mb-1 opacity-70">
            {type === 'movie' ? (
              <Film className="w-3.5 h-3.5" />
            ) : type === 'series' ? (
              <Layers className="w-3.5 h-3.5" />
            ) : (
              <Tv className="w-3.5 h-3.5" />
            )}
            {category && (
              <span className="text-[9px] font-medium tracking-tight uppercase truncate max-w-[80px]">
                {category}
              </span>
            )}
          </div>
          <span className="font-extrabold font-mono tracking-wider drop-shadow-sm truncate max-w-full text-center px-1">
            {badgeText}
          </span>
        </div>
      )}
    </div>
  );
};
